import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { getProcurementPlanDetail } from '@ebalance/proto/bffuser/v1/procurement_plan_service-ProcurementPlanService_connectquery'
import {
  GetProcurementPlanDetailRequestSchema,
  type GetProcurementPlanDetailResponse,
} from '@ebalance/proto/bffuser/v1/procurement_plan_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

/** 詳細 polling の間隔 (ms)。一覧 polling (useListProcurementPlans) と同じ間隔。 */
const IN_FLIGHT_POLL_INTERVAL_MS = 5_000

/**
 * SUBMITTED polling の打ち切り窓 (ms、= 1 時間)。backend の受領照合
 * safety valve `reconcileStuckWarnAfter`
 * (`go/internal/backend/application/usecase/plansubmit/worker_reconcile.go`)
 * と同じ 1 時間を採用する: 通常の OCCTO 受付 latency は数分で、1 時間を
 * 超えて未確定のまま残るケースは reconcile worker 側の運用検知（stuck 警告）の
 * 領分であり、frontend が polling を続ける対象ではない (plansubmit-error-detail
 * design 決定 #4 改訂)。
 */
const SUBMITTED_POLL_WINDOW_MS = 60 * 60 * 1000

/**
 * ProcurementPlanService.GetProcurementPlanDetail を呼ぶ hook。
 * response をそのまま返す（`detail.retailerBreakdowns` = 配下小売電気事業者の内訳。
 * 旧 top-level `retailers` (RetailerInfo) は deprecated のため非使用）。
 *
 * global QueryClient の `throwOnError: true` を本 query だけ `false` に上書きする。
 * 取得失敗は AC-PPD-001a のとおり呼び出し元がページ内の Alert + 再読み込み導線で
 * 扱うため、ErrorLayout (全画面エラー) に飛ばさない。
 * 注意: テスト用 QueryClient (testUtils) は throwOnError: false を強制するため、
 * この上書きの欠落は F5 テストでは検出できない (プロダクトのみで全画面エラー化する)。
 *
 * 条件付き polling (plansubmit-error-detail design 決定 #4 改訂、案 A):
 * - status が GENERATING (作成中) / SUBMITTING (処理中) の間は 5 秒間隔で再取得
 * - status が SUBMITTED (提出済・受領照合待ち) の間も、`updatedAt` から
 *   `SUBMITTED_POLL_WINDOW_MS` (1 時間) 以内なら 5 秒間隔で再取得を続け、
 *   REJECTED への非同期遷移 (AC-PLC-021、バッジ・エラー詳細表示の更新) に追随する
 *   (トーストは一覧のみ、詳細はバッジ表示の更新のみ)。1 時間を超えたら polling
 *   off (`refetchInterval` は評価毎に呼ばれる関数形式のため、経過後は自然停止する)
 * - それ以外の status では polling off
 */
export function useGetProcurementPlanDetail(
  id: string,
): UseQueryResult<GetProcurementPlanDetailResponse, ConnectError> {
  return useQuery(
    getProcurementPlanDetail,
    create(GetProcurementPlanDetailRequestSchema, { id }),
    {
      enabled: id !== '',
      throwOnError: false,
      refetchInterval: (query) => {
        const detail = query.state.data?.detail
        const status = detail?.status
        // AC-PPP-019: 約定結果が未着の札がある間は、0 件の応答が返るまで再取得を続ける。
        if ((detail?.bidsAwaitingMatchResultCount ?? 0) > 0) {
          return IN_FLIGHT_POLL_INTERVAL_MS
        }
        if (
          status === PlanSubmissionStatus.GENERATING ||
          status === PlanSubmissionStatus.SUBMITTING ||
          status === PlanSubmissionStatus.APPROVAL_PENDING
        ) {
          return IN_FLIGHT_POLL_INTERVAL_MS
        }
        if (status === PlanSubmissionStatus.SUBMITTED) {
          const updatedAt = detail?.updatedAt
          if (updatedAt === undefined) {
            return false
          }
          // ここだけ now() (サーバー時刻 = ユーザーの偽装時刻) ではなく実時刻を使う。
          // `updatedAt` は reconcile worker が worker 自身の context で書く監査時刻で、
          // ユーザーの偽装時刻は載らない。偽装時刻と引き算すると未来偽装で提出直後に
          // polling が止まり、過去偽装で経過時間が負のまま無期限に polling が続く。
          // これは業務日付の判定ではなく「処理待ちの経過時間」の判定なので、偽装の
          // 載らない実時刻どうしで引くのが正しい。
          //
          // ただし `updatedAt` は worker (サーバー) の実時計、`Date.now()` はブラウザの
          // 実時計で、別マシンの時計であることは閉じていない。ブラウザ時計が大きく
          // 狂っている端末では polling window の判定がその狂いぶんズレる (進んでいれば
          // 早期停止、遅れていれば window を超えても polling が続く)。ここを完全に
          // 閉じるには「SUBMITTED を最初に観測してからの経過時間」で測る必要があり、
          // 本 hook に観測時刻の state を持ち込むことになるため別途対応とする。
          const elapsedMs = Date.now() - timestampDate(updatedAt).getTime()
          return elapsedMs < SUBMITTED_POLL_WINDOW_MS
            ? IN_FLIGHT_POLL_INTERVAL_MS
            : false
        }
        return false
      },
    },
  )
}
